import { Component, Input, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { HttpClient } from '@angular/common/http';
import { ADMIN_API_SERVER_URL } from 'app/server/services/tokens/admin-api-server-url.token';
import { Inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { ProductData, UnitSize } from '@equipmyschool/emsadminweb-models';
import { ProductService, StatusService, UnitSizeService } from 'app/server/server.module';
import { NotesDialogComponent } from 'app/coad-web/dispatch-details/pages/dialogs/notes-dialog/notes-dialog.component';

@Component({
  selector: 'app-data-access-products',
  templateUrl: './data-access-products.component.html',
  styleUrl: './data-access-products.component.scss'
})
export class DataAccessProductsComponent {
  @Input() id: number | null;
  @Input() mode: 'view' | 'edit' | 'search' = 'view';

  private _initProduct: ProductData | null = null;

  private _id$ = new BehaviorSubject<number>(null);
  public id$ = this._id$.asObservable();
  public product$: Observable<ProductData> = this.productService.product$;
  public currentUnit$: Observable<UnitSize>;
  public currentUnitString$: Observable<string>;

  public selectedCode: string;
  public tradePrice: number;
  public manualPricing: boolean = false;
  public pricingMode: string = 'discount';
  public imageSourceMode: string = 'link';

  constructor(
    private dialog: MatDialog,
    private productService: ProductService,
    private statusService: StatusService,
    private http: HttpClient,
    @Inject(ADMIN_API_SERVER_URL) private serverUrl: string
  ) {}
  private UnitService = new UnitSizeService(this.http, this.serverUrl);

  ngOnInit() {
    // initialize product stream when id changes (handled in ngOnChanges)
    this.id$.pipe(tap(id => {
      if (id) {
        this.productService.getProduct(id);
      }
      else {
        this.productService.newProduct();
      }
    })).subscribe();
    this.currentUnit$ = this.UnitService.UnitSize$;
    
    this.product$.pipe(tap(product => {
      if (product) {
        this._initProduct = product;
        this.tradePrice = this.roundNumber(product.Cost * (1 - product.Discount), 2);
        if (product.UnitType) this.UnitService.getUnitSize(product.UnitType);
      }
    })).subscribe();

    this.currentUnitString$ = combineLatest([this.product$, this.currentUnit$]).pipe(
      map(([product, unit]) => {
        let unitName = "";        
        if (!product || !unit) return unitName;
        if (!unit.UnitSizeRequest || product.UnitAmount == 1) {
          unitName = unit.SingleName;
        } else {
          switch (unit.Prefix) {
            case 0:
              unitName = unit.PluralName + " ##";
              break;
            case 1:
              unitName = "## " + unit.PluralName;
              break;
            case 2:
              unitName = unit.PluralName;
              break;
            default:
              unitName = unit.Name;
          }
        }

        return unitName.replace("##", String(product.UnitAmount));
      })
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.id) {
      this._id$.next(changes.id.currentValue);
    }
  }

  async viewNotes() {
    const product = await firstValueFrom(this.productService.product$);
    if (product && product.ProductID) {
        this.dialog.open(NotesDialogComponent, {
          id: 'notes-dialog',
          width: '560px',
          disableClose: false,
          panelClass: 'confirmation-pane',
          data: {
            tableName: 'products',
            id: product.ProductID
          }
        });
    }
  }

  selectCode(code: string)
  {
    this.selectedCode = code;
  }

  addCode()
  {
    const code = prompt('Enter the code to add');
    // If a code was entered
    if (code)
    {
      // Add it to the array if it doesn't already exist
      this.product$.pipe(tap(product => {
        if (product && !product.ProductCodes?.includes(code)) {
          product.ProductCodes = product.ProductCodes || [];
          product.ProductCodes.push(code);
        }
      })).subscribe();
    }
  }

  deleteCode()
  {
    this.product$.pipe(tap(product => { if (product) product.ProductCodes = product.ProductCodes.filter(c => c != this.selectedCode) })).subscribe();
    this.selectedCode = null;
  }

  async validate(): Promise<boolean> {
    var allOk = true;
    const product = await firstValueFrom(this.product$);
    const status = (await firstValueFrom(this.statusService.statuses$)).find(s => s.StatusID == product.Status);

    if (
      product.Cost != this._initProduct.Cost ||
      product.Discount != this._initProduct.Discount ||
      product.Price != this._initProduct.Price
    ) {
      if (this._initProduct.PricingExpiry && this._initProduct.PricingExpiry > new Date()) {
        if (!window.confirm('You are trying to update the pricing on a product with valid pricing. Are you absolutely sure you wish to continue?')) {
          return false;
        }
      }
      this.confirmPricing();
    }

    if (!product.Supplier) {
      alert('Supplier is required');
      allOk = false;
    }
    else if (!product.Code) {
      alert('Code is required');
      allOk = false;
    }
    else if (!product.Description) {
      alert('Description is required');
      allOk = false;
    }
    else if (await this.checkForDuplicateProduct()) {
      allOk = false;
    }
    else if (!product.Commodity) {
      alert('Commodity is required');
      allOk = false;
    }
    else if (!product.Currency) {
      alert('Currency is required');
      allOk = false;
    }
    else if (!product.UnitType) {
      alert('Unit Type is required');
      allOk = false;
    }
    else if (!product.UnitAmount || product.UnitAmount <= 0) {
      alert('Unit Amount must be a number greater than 0');
      allOk = false;
    }
    else if (!status.NotAvailable && (!product.PricingExpiry || product.PricingExpiry < new Date())) {
      alert('Please update the pricing or use the Pricing Confirmed Ok button.');
      allOk = false;
    }

    return allOk;
  }

  async checkForDuplicateProduct(): Promise<boolean> {
    const product = await firstValueFrom(this.product$);
    return new Promise<boolean>((resolve, reject) => {
      this.productService.checkForDuplicateProduct(product.Supplier, product.Code, product.ProductID).subscribe({
        next: (isDuplicate) => {
          if (isDuplicate) {
            alert('There is already a product with that code for the selected supplier in the database');
          }
          resolve(isDuplicate);
        },
        error: (err) => {
          console.error('Error checking for duplicate product', err);
          reject(err);
        }
      });
    });
  }

  async save(): Promise<ProductData> {
    const product = await firstValueFrom(this.product$);
    const url = `${this.serverUrl}Product/Save`;
    const saved = await firstValueFrom(this.http.post<ProductData>(url, product));
    if (saved && saved.ProductID) {
      this.productService.getProduct(saved.ProductID);
    }
    return saved;
  }

  tradePriceChanged(target: any) {
    const value = parseFloat(target.value);
    if (!isNaN(value)) {
      this.tradePrice = value;
      this.product$.pipe(tap(product => {
        if (product) {
          product.Discount = this.roundNumber((product.Cost - this.tradePrice) / product.Cost, 2)
          this.updatePrice();
        }
      })).subscribe();
    }
  }

  discountChanged(target: any) {
    const value = parseFloat(target.value);
    if (!isNaN(value)) {
      this.product$.pipe(tap(product => {
        if (product) {
          product.Discount = value;
          while (product.Discount > 1) {
            product.Discount /= 100;
          }
          this.tradePrice = this.roundNumber(product.Cost * (1 - product.Discount), 2);
          this.updatePrice();
        }
      })).subscribe();
    }
  }

  manualPricingChanged(event: any) {
    this.manualPricing = event.checked;
  }

  public updatePrice() {
    this.product$.pipe(tap(product => {
      if (product) {
        var dblDisc = product.Discount;
        var dblVal = product.Cost;
        var prodMU = 0;

        if (dblVal == 0) {
          dblVal = this.tradePrice;
          prodMU = 0.3;
        }
        else if (dblDisc >= 0.25) {
          prodMU = 0;
        }
        else if (1 - ((1 - dblDisc) * (1 - 0.1)) < 0.25) {
          prodMU = 0.1;
        }
        else {
          prodMU = 1 - ((1 - 0.25) / (1 - dblDisc))
        }

        if (prodMU) dblVal = this.normaliseValue(dblVal / (1 - prodMU));
        product.Price = dblVal;
      }
    })).subscribe();
  }

  public markUp10(): void {
    this.markUpPricing(0.1);
  }

  public markUp15(): void {
    this.markUpPricing(0.15);
  }

  public markUp20(): void {
    this.markUpPricing(0.2);
  }

  public markUp25(): void {
    this.markUpPricing(0.25);
  }

  private markUpPricing(value: number): void {
    this.product$.pipe(tap(product => { if (product) product.Price = this.normaliseValue(this.tradePrice / (1 - value)); })).subscribe();
  }

  public confirmPricing(): void {
    this.product$.pipe(tap(product => {
      if (product) {
        var tmpDate = new Date();
        product.PricingExpiry = new Date(
          tmpDate.getUTCFullYear(),
          tmpDate.getUTCMonth() + 3,
          tmpDate.getUTCDate()
        );
        product.LastImported = null;
      }
    })).subscribe();
  }

  public roundNumber(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals);
    return (Math.round(value * factor) / factor).toFixed(decimals) as unknown as number;
  }

  public normaliseValue(value: number): number {
    var strVal = value.toFixed(2);
    var retValue = strVal as unknown as number;

    switch (strVal.substring(strVal.length - 1, strVal.length)) {
      case "1":
      case "2":
      case "3":
      case "4":
        retValue = strVal.substring(0, strVal.length - 1) + "5" as unknown as number;
        break;
      case "6":
      case "7":
      case "8":
        retValue = strVal.substring(0, strVal.length - 1) + "9" as unknown as number;
        break;
    }

    return retValue;
  }
}